// /utils/rest-api/fetcher.ts
import type { IRestResponse, JsonInit } from './types';

export interface UserError<T = unknown> extends Error {
    status?: number;
    response?: IRestResponse<T>;
    sendResponse?: true;
}

// ----------------- small helpers (no any) -----------------
function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null;
}

function toRequestBody(input: unknown): string | undefined {
    if (input == null) return undefined;                 // undefined for fetch
    if (typeof input === 'string') return input;         // pass-through string
    return JSON.stringify(input);                        // JSON for objects/arrays/other primitives
}

export class Fetcher {
    /**
     * Always return an IRestResponse<T>.
     * - When doWrap=true, we wrap raw payloads.
     * - When doWrap=false, we assume the server already returns IRestResponse<T>.
     */
    private wrapResponse<T>(payload: unknown): IRestResponse<T> {
        return payload as IRestResponse<T>;
    }

    public fetch = async <T>(url: string, init?: RequestInit): Promise<IRestResponse<T>> => {
        const res  = await fetch(url, init);
        const json = this.wrapResponse<T>(await res.json());

        if (!res.ok) {
            const err: UserError<T> = new Error(`${res.status}: ${json?.message ?? res.statusText}`);
            err.status = res.status;
            throw err;
        }
        if (json && json.isSuccess === false) {
            const err: UserError<T> = new Error(json.message || 'Request failed');
            err.sendResponse = true;
            err.response = json;
            throw err;
        }
        return json;
    };

    public fetchWithMutation = async <T>(
        url: string,
        { arg }: { arg: JsonInit }
    ): Promise<IRestResponse<T>> => {
        const init: RequestInit = {
            method  : arg?.method ?? 'POST',
            headers : { 'Content-Type': 'application/json', ...(arg?.headers ?? {}) },
            body    : toRequestBody(arg?.body),
        };
        return this.fetch<T>(url, init);
    };
}
